import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "content"
}
const _hoisted_2 = { class: "footer" }
const _hoisted_3 = { class: "self-certification-details-form" }
const _hoisted_4 = {
  class: "label-wrapper",
  field: "costOfAttendance"
}
const _hoisted_5 = { class: "tuition-costs" }
const _hoisted_6 = {
  class: "form-group",
  columns: "2"
}
const _hoisted_7 = {
  class: "form-group",
  columns: "3"
}
const _hoisted_8 = { class: "footer justify-content-flex-end" }
const _hoisted_9 = { class: "form-error" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = ["href"]
const _hoisted_12 = ["href"]
const _hoisted_13 = { class: "footer-btn-group" }

import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useStore } from "vuex";
import Modal from "@/components/shared/Modal.vue";
import NextButton from "@/components/shared/NextButton.vue";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import type { School } from "@/models/school";
import { AddressCountryIds, CustomSchoolTypes } from "@/config/constants";
import type { FieldSchema, FormSchema, Option } from "@/models/form";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { $api } from "@/services/api";
import useValidation from "@/use/validation";
import type { Stipulation } from "@/models/opal";
import type { StipulationInputRequest, SelfCertificationDetailsInputRequest } from "@/models/api";
import useApplicationData from "@/composables/useApplicationData";

interface TuitionCosts {
  inState: number | string;
  outOfState: number | string;
}

interface Form {
  schoolId: number;
  inState: boolean;
  costOfAttendance?: string | number;
  estimatedFinancialAssistance?: string | number;
  address1?: string;
  address2?: string;
  city?: string;
  stateId?: number;
  countryId?: number;
  postalCode?: string;
}

interface Props {
  stip: Stipulation;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SelfCertificationDetailsFormModal',
  props: {
    stip: {}
  },
  emits: ["success"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const { validateForm } = useValidation();

const store = useStore();

const props = __props;

const emit = __emit;

const { applicationId, tollFreeNumber, hrefTFN, emailAddress, productTypeId } =
  useApplicationData();

const submissionError = ref<boolean>(false);

const validForm = ref<boolean>(true);

const formError = computed<boolean>(() => validForm.value === false);

const formErrorMessage = computed<string>(() =>
  formError.value ? "Missing information above. Please review and complete before continuing." : "",
);

const states = computed<Option[]>(() => store.getters["lookupsModule/statesOptions"]);

const schools = computed<School[]>(() => store.state.schools);

const confirmChanges = ref<boolean>(false);

const savingChanges = ref<boolean>(false);

const loading = ref<boolean>(true);

const tuitionType = ref<string>("in-state");

const header = computed<string>(() =>
  confirmChanges.value ? "Self-Certification Details Confirmed" : "Self-Certification Details",
);

const inState = computed<boolean>(() => tuitionType.value === "in-state");

const selfCertificationDetailsInput = computed(() => props.stip?.selfCertificationDetailsInput);

const applicationStipulationId = computed<number>(() => props.stip?.id ?? 0);

const selectedSchool = computed(() =>
  schools.value.find(
    (s: School) => s.id === store.state.appData?.application?.schoolConfiguration?.schoolId,
  ),
);

const selectedEnrollmentTerm = computed<string>(() => {
  return (
    store.state.loanConfigurationModule?.applicationInfo?.selectedEnrollmentTerm?.description ||
    store.state.appData?.application?.schoolConfiguration?.academicTerm
  );
});

const combinedTerms = computed<boolean>(() => (selectedEnrollmentTerm.value || "").includes("-"));

const tuitionCosts = computed<TuitionCosts>(() => {
  let tuitionCosts = {
    inState: 0,
    outOfState: 0,
  } as TuitionCosts;

  //#TODO handle custom program tuitions with their custom terms like Aviation
  if (selectedSchool.value && CustomSchoolTypes.includes(selectedSchool.value?.schoolTypeId)) {
    tuitionCosts.inState = selectedSchool.value?.costOfAttendanceInState ? selectedSchool.value?.costOfAttendanceInState.toFixed(2) : 0;
    tuitionCosts.outOfState = selectedSchool.value?.costOfAttendanceOutOfState ? selectedSchool.value?.costOfAttendanceOutOfState.toFixed(2) : 0;

    return tuitionCosts;
  }

  tuitionCosts.inState = selectedSchool.value?.costOfAttendanceInState
    ? combinedTerms.value === true
      ? selectedSchool.value?.costOfAttendanceInState.toFixed(2)
      : (selectedSchool.value?.costOfAttendanceInState / 2).toFixed(2)
    : 0;

  tuitionCosts.outOfState = selectedSchool.value?.costOfAttendanceOutOfState
    ? combinedTerms.value === true
      ? selectedSchool.value?.costOfAttendanceOutOfState.toFixed(2)
      : (selectedSchool.value?.costOfAttendanceOutOfState / 2).toFixed(2)
    : 0;

  return tuitionCosts;
});

let form = ref<Form>({
  schoolId: computed<number>(() => store.state.appData.application.schoolConfiguration?.schoolId)
    .value,
  inState: true,
  costOfAttendance: undefined,
  estimatedFinancialAssistance: undefined,
  address1: undefined,
  address2: undefined,
  city: undefined,
  stateId: undefined,
  postalCode: undefined,
  countryId: AddressCountryIds.USA,
});

const schema = ref<FormSchema>({
  costOfAttendance: {
    name: "costOfAttendance",
    label: "Students cost of attendance for the period of enrollment covered by the loan",
    type: "money",
    hideLabel: false,
    required: true,
    allowZero: false,
    errorMessage: computed(() =>
      form.value.costOfAttendance && Number(form.value.costOfAttendance) > 0
        ? ""
        : "Cost of attendance is required.",
    ).value,
    allowDecimals: true,
    productTypeId: computed(() => productTypeId.value).value,
    showRequiredIndicator: true,
  } as FieldSchema,
  estimatedFinancialAssistance: {
    name: "estimatedFinancialAssistance",
    label: "Estimated financial assistance for the period of enrollment covered by the loan",
    type: "money",
    required: true,
    allowZero: true,
    allowDecimals: true,
    showRequiredIndicator: true,
    errorMessage: computed(() =>
      form.value.estimatedFinancialAssistance && Number(form.value.estimatedFinancialAssistance) > 0
        ? ""
        : "Please provide a valid amount, or enter 0.",
    ).value,
  },
  address1: {
    name: "address1",
    label: "School Address 1",
    type: "text",
    required: true,
    showRequiredIndicator: true,
  },
  address2: {
    name: "address2",
    type: "text",
    subtype: "address",
    label: "School Address 2",
    required: false,
    showRequiredIndicator: true,
  } as FieldSchema,
  city: {
    name: "city",
    type: "text",
    label: "City",
    subtype: "address",
    required: true,
    showRequiredIndicator: true,
  } as FieldSchema,
  stateId: {
    name: "stateId",
    type: "select",
    subtype: "address",
    label: "State",
    required: true,
    showRequiredIndicator: true,
  } as FieldSchema,
  postalCode: {
    name: "postalCode",
    type: "zipcode",
    label: "Zip Code",
    errorMessage: "Zip Code is required.",
    required: true,
    showRequiredIndicator: true,
  } as FieldSchema,
});

function closeModal() {
  if (confirmChanges.value === true) {
    store.dispatch("checklistModule/updateStipVuexState", {
      stipulation: props.stip,
      title: "Regulatory Verification",
    });

    const body = document.querySelector("body");
    if (body) {
      body.style.overflow = "visible";
    }
  }

  store.dispatch("modal/closeSelfCertificationDetailsModal");

  savingChanges.value = false;
  submissionError.value = false;
  confirmChanges.value = false;
}

function currencyToDecimal(currency: string | number) {
  let decimal;
  if (typeof currency === "number") {
    decimal = currency;
  } else {
    const numberString = currency.replace(/[^0-9.-]+/g, "");
    decimal = parseFloat(numberString);
  }

  return isNaN(decimal) ? 0 : decimal;
}

function resetForm() {
  form.value.inState = true;
  form.value.costOfAttendance = undefined;
  form.value.estimatedFinancialAssistance = undefined;
  form.value.address1 = undefined;
  form.value.address2 = undefined;
  form.value.city = undefined;
  form.value.stateId = undefined;
  form.value.postalCode = undefined;
}

function cancelChanges() {
  resetForm();
  closeModal();
}

async function saveChanges() {
  savingChanges.value = true;
  submissionError.value = false;

  validForm.value = validateForm(schema.value, form.value);

  if (validForm.value) {
    form.value.inState = inState.value;
    form.value.costOfAttendance = form.value.costOfAttendance
      ? currencyToDecimal(form.value.costOfAttendance)
      : form.value.costOfAttendance;

    form.value.estimatedFinancialAssistance = form.value.estimatedFinancialAssistance
      ? currencyToDecimal(form.value.estimatedFinancialAssistance)
      : form.value.estimatedFinancialAssistance;

    const request = {
      applicationStipulationId: applicationStipulationId.value,
      applicationId: applicationId.value,
      selfCertificationDetailsInput: form.value as SelfCertificationDetailsInputRequest,
    } as StipulationInputRequest;

    const response = await $api.applications.addStipInput(applicationStipulationId.value, request);

    if (response?.id && response?.id > 0) {
      confirmChanges.value = true;
      savingChanges.value = false;

      if (props.stip) {
        let updatedStip = props.stip;
        updatedStip.stipulationStatusId = response.stipulationStatusId;
        updatedStip.stipulationStatusName = response.stipulationStatusName;
        emit("success", updatedStip);
        await nextTick();
      }
    } else {
      confirmChanges.value = false;
      savingChanges.value = false;
      submissionError.value = true;
    }
  } else {
    savingChanges.value = false;
  }
}

function setCostOfAttendance() {
  form.value.costOfAttendance =
    tuitionType.value === "out-of-state"
      ? tuitionCosts.value.outOfState
      : tuitionCosts.value.inState;

  schema.value.costOfAttendance.errors = [];
  schema.value.costOfAttendance.valid = true;
}

onMounted(async () => {
  if ((store.state.schools || []).length <= 0) {
    const res = await $api.school.getSchoolsByProgram(
      store?.state?.appData?.application?.programTypeId,
      store?.state?.appData?.application?.productTypeId,
      store?.state?.appData?.application?.lenderId,
    );

    store.dispatch("setSchools", res.schools);
  }

  setCostOfAttendance();

  nextTick(() => {
    loading.value = false;
  });
});

watch(
  () => tuitionType.value,
  () => {
    setCostOfAttendance();
  },
);

__expose({
  selfCertificationDetailsInput,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    class: _normalizeClass(["self-certification-modal", confirmChanges.value ? 'confirm' : '']),
    closeBtn: true,
    onClose: closeModal
  }, {
    default: _withCtx(() => [
      (loading.value)
        ? (_openBlock(), _createBlock(CircleLoader, { key: 0 }))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("div", {
              class: _normalizeClass(["header", confirmChanges.value ? 'text-center' : ''])
            }, _toDisplayString(header.value), 3),
            (confirmChanges.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                  _cache[9] || (_cache[9] = _createElementVNode("p", null, " Thank you for providing the self-certification details. The Self-Certification form will be included in your final loan documents. ", -1)),
                  _createElementVNode("div", _hoisted_2, [
                    _createVNode(NextButton, {
                      onClick: closeModal,
                      text: "Close",
                      class: "next-btn",
                      disabled: savingChanges.value,
                      loading: savingChanges.value
                    }, null, 8, ["disabled", "loading"])
                  ])
                ]))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(BaseFormField, {
                      modelValue: _unref(form).costOfAttendance,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(form).costOfAttendance) = $event)),
                      schema: schema.value.costOfAttendance
                    }, {
                      label: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                          _cache[10] || (_cache[10] = _createElementVNode("div", null, [
                            _createTextVNode(" Students cost of attendance for the period of enrollment covered by the loan "),
                            _createElementVNode("span", { class: "required" }, "*")
                          ], -1)),
                          _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("p", {
                              class: _normalizeClass({ active: tuitionType.value === 'in-state' }),
                              onClick: _cache[0] || (_cache[0] = ($event: any) => (tuitionType.value = 'in-state')),
                              "data-value": "in-state"
                            }, " In State ", 2),
                            _createElementVNode("p", {
                              class: _normalizeClass({ active: tuitionType.value === 'out-of-state' }),
                              "data-value": "out-of-state",
                              onClick: _cache[1] || (_cache[1] = ($event: any) => (tuitionType.value = 'out-of-state'))
                            }, " Out of State ", 2)
                          ])
                        ])
                      ]),
                      _: 1
                    }, 8, ["modelValue", "schema"]),
                    _createVNode(BaseFormField, {
                      modelValue: _unref(form).estimatedFinancialAssistance,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(form).estimatedFinancialAssistance) = $event)),
                      schema: schema.value.estimatedFinancialAssistance
                    }, null, 8, ["modelValue", "schema"]),
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(BaseFormField, {
                        modelValue: _unref(form).address1,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(form).address1) = $event)),
                        schema: schema.value.address1
                      }, null, 8, ["modelValue", "schema"]),
                      _createVNode(BaseFormField, {
                        modelValue: _unref(form).address2,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(form).address2) = $event)),
                        schema: schema.value.address2
                      }, null, 8, ["modelValue", "schema"])
                    ]),
                    _createElementVNode("div", _hoisted_7, [
                      _createVNode(BaseFormField, {
                        modelValue: _unref(form).city,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(form).city) = $event)),
                        schema: schema.value.city
                      }, null, 8, ["modelValue", "schema"]),
                      _createVNode(BaseFormField, {
                        modelValue: _unref(form).stateId,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(form).stateId) = $event)),
                        modelModifiers: { number: true },
                        schema: schema.value.stateId,
                        options: states.value
                      }, null, 8, ["modelValue", "schema", "options"]),
                      _createVNode(BaseFormField, {
                        modelValue: _unref(form).postalCode,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(form).postalCode) = $event)),
                        schema: schema.value.postalCode
                      }, null, 8, ["modelValue", "schema"])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, [
                      _createTextVNode(_toDisplayString(formErrorMessage.value) + " ", 1),
                      (submissionError.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_10, [
                            _cache[11] || (_cache[11] = _createTextVNode(" Your changes could not be saved. Please try again. If the issue persists, please contact our support team at ", -1)),
                            _createElementVNode("a", {
                              class: "underline",
                              href: `tel:${_unref(hrefTFN)}`
                            }, _toDisplayString(_unref(tollFreeNumber)), 9, _hoisted_11),
                            _cache[12] || (_cache[12] = _createTextVNode(" or ", -1)),
                            _createElementVNode("a", {
                              class: "underline",
                              href: `mailto:${_unref(emailAddress)}`
                            }, _toDisplayString(_unref(emailAddress)), 9, _hoisted_12),
                            _cache[13] || (_cache[13] = _createTextVNode(". ", -1))
                          ]))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      (!confirmChanges.value)
                        ? (_openBlock(), _createBlock(NextButton, {
                            key: 0,
                            disabled: savingChanges.value,
                            text: "Cancel",
                            class: "cancel-btn",
                            onClick: cancelChanges
                          }, null, 8, ["disabled"]))
                        : _createCommentVNode("", true),
                      _createVNode(NextButton, {
                        onClick: saveChanges,
                        text: "Save",
                        class: "next-btn",
                        disabled: savingChanges.value,
                        loading: savingChanges.value
                      }, null, 8, ["disabled", "loading"])
                    ])
                  ])
                ], 64))
          ], 64))
    ]),
    _: 1
  }, 8, ["class"]))
}
}

})